import React, { useEffect, useState } from "react";
import { Box, Button, Divider, Typography } from "@mui/material";

/**
 * Apple Pay / Google Pay buttons for the QorCommerce checkout (web).
 *
 * These offer a one-tap alternative to the PayFrame card iframe. On success the
 * wallet returns a payment token + a wallet payload; we hand both back via
 * onWallet({ token, wallet }). The parent then calls placeOrder with
 * qorPaymentToken = token and qorWallet = JSON.stringify(wallet), and the
 * backend forwards the wallet object to the QorCommerce sale (chargeSaleToken).
 *
 * ⚠️ PRODUCTION-VALIDATION ONLY: Apple Pay & Google Pay have no usable sandbox
 * here — they are validated only in production (like the gwapi document upload).
 * So Google Pay defaults to the PRODUCTION environment (NOT "TEST"), and the
 * buttons stay hidden until the real production wallet config below is present.
 *
 * Wallet provisioning required before these light up (all done in production):
 *   - Apple Pay: a Merchant ID + verified domain (Apple Pay JS merchant
 *     validation, normally proxied through QorCommerce). REACT_APP_QOR_APPLE_MERCHANT_ID.
 *   - Google Pay: a merchant id + the QorCommerce gateway tokenization params.
 *     REACT_APP_QOR_GOOGLE_MERCHANT_ID / REACT_APP_QOR_GATEWAY / REACT_APP_QOR_GATEWAY_MID.
 * Buttons only render when their platform + config are available, so this is
 * safe to always mount.
 */
const APPLE_MERCHANT_ID = process.env.REACT_APP_QOR_APPLE_MERCHANT_ID;
const GOOGLE_MERCHANT_ID = process.env.REACT_APP_QOR_GOOGLE_MERCHANT_ID;
const GATEWAY = process.env.REACT_APP_QOR_GATEWAY || "qorcommerce";
const GATEWAY_MID = process.env.REACT_APP_QOR_GATEWAY_MID;
// Production-validation only — default to PRODUCTION. Override to TEST only for
// local experimentation; real wallet validation happens in production.
const GOOGLE_ENV = process.env.REACT_APP_QOR_GOOGLE_ENV || "PRODUCTION"; // PRODUCTION | TEST

export default function QorWalletButtons({ amount, onWallet, onError }) {
  const [appleAvailable, setAppleAvailable] = useState(false);
  const [googleClient, setGoogleClient] = useState(null);

  // Apple Pay: available only on Safari/Apple devices with an active card.
  useEffect(() => {
    try {
      if (
        APPLE_MERCHANT_ID &&
        typeof window !== "undefined" &&
        window.ApplePaySession &&
        window.ApplePaySession.canMakePayments()
      ) {
        setAppleAvailable(true);
      }
    } catch (_) { /* not available */ }
  }, []);

  // Google Pay: load pay.js, then probe isReadyToPay.
  useEffect(() => {
    if (!GOOGLE_MERCHANT_ID) return;
    const init = () => {
      try {
        if (!window.google || !window.google.payments) return;
        const client = new window.google.payments.api.PaymentsClient({
          environment: GOOGLE_ENV,
        });
        const baseRequest = {
          apiVersion: 2,
          apiVersionMinor: 0,
          allowedPaymentMethods: [
            {
              type: "CARD",
              parameters: {
                allowedAuthMethods: ["PAN_ONLY", "CRYPTOGRAM_3DS"],
                allowedCardNetworks: ["VISA", "MASTERCARD", "AMEX", "DISCOVER"],
              },
              tokenizationSpecification: {
                type: "PAYMENT_GATEWAY",
                parameters: { gateway: GATEWAY, gatewayMerchantId: GATEWAY_MID || "" },
              },
            },
          ],
        };
        client
          .isReadyToPay(baseRequest)
          .then((res) => { if (res.result) setGoogleClient({ client, baseRequest }); })
          .catch(() => {});
      } catch (_) { /* ignore */ }
    };
    if (window.google && window.google.payments) { init(); return; }
    const existing = document.querySelector('script[data-qor-gpay="1"]');
    if (existing) { existing.addEventListener("load", init); return; }
    const s = document.createElement("script");
    s.src = "https://pay.google.com/gp/p/js/pay.js";
    s.async = true;
    s.dataset.qorGpay = "1";
    s.onload = init;
    document.body.appendChild(s);
  }, []);

  async function payApple() {
    try {
      const request = {
        countryCode: "US",
        currencyCode: "USD",
        supportedNetworks: ["visa", "masterCard", "amex", "discover"],
        merchantCapabilities: ["supports3DS"],
        total: { label: "RushmyFood", amount: Number(amount || 0).toFixed(2) },
      };
      const session = new window.ApplePaySession(3, request);
      session.onvalidatemerchant = async (event) => {
        // Merchant validation is normally proxied by the gateway. If not wired
        // yet this will reject — surface a clear message instead of hanging.
        try {
          const resp = await fetch(
            `${process.env.REACT_APP_SERVER_URL || ""}/qor/apple-pay/validate`,
            { method: "POST", headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ validationURL: event.validationURL }) }
          );
          const merchantSession = await resp.json();
          session.completeMerchantValidation(merchantSession);
        } catch (e) {
          session.abort();
          if (onError) onError("Apple Pay isn't fully set up yet. Please use a card.");
        }
      };
      session.onpaymentauthorized = (event) => {
        const token = JSON.stringify(event.payment.token); // opaque wallet token
        session.completePayment(window.ApplePaySession.STATUS_SUCCESS);
        // Per QorPay (Nate, 2026-07-10): apple_pay must contain ONLY the
        // contents of Apple's paymentData object — not the full payment token.
        if (onWallet) onWallet({ token, wallet: { apple_pay: event.payment.token.paymentData } });
      };
      session.begin();
    } catch (e) {
      if (onError) onError(e.message || "Apple Pay failed.");
    }
  }

  async function payGoogle() {
    try {
      const { client, baseRequest } = googleClient;
      const paymentDataRequest = {
        ...baseRequest,
        merchantInfo: { merchantId: GOOGLE_MERCHANT_ID, merchantName: "RushmyFood" },
        transactionInfo: {
          totalPriceStatus: "FINAL",
          totalPrice: Number(amount || 0).toFixed(2),
          currencyCode: "USD",
          countryCode: "US",
        },
      };
      const paymentData = await client.loadPaymentData(paymentDataRequest);
      const token = paymentData.paymentMethodData.tokenizationData.token;
      if (onWallet) onWallet({ token, wallet: { google_pay: paymentData.paymentMethodData } });
    } catch (e) {
      if (e && e.statusCode === "CANCELED") return;
      if (onError) onError((e && e.message) || "Google Pay failed.");
    }
  }

  if (!appleAvailable && !googleClient) return null;

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {appleAvailable && (
          <Button fullWidth variant="contained"
            sx={{ bgcolor: "#000", color: "#fff", "&:hover": { bgcolor: "#222" }, py: 1.2 }}
            onClick={payApple}>
             Pay
          </Button>
        )}
        {googleClient && (
          <Button fullWidth variant="outlined" sx={{ py: 1.2 }} onClick={payGoogle}>
            Pay with Google Pay
          </Button>
        )}
      </Box>
      <Divider sx={{ my: 1.5 }}>
        <Typography variant="caption" color="text.secondary">or pay with card</Typography>
      </Divider>
    </Box>
  );
}
